import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  taxService,
  TaxCalculation,
  TaxCalculationInput,
  TaxLineInput,
} from '../services/taxService';

/**
 * THE tax hook every transaction form uses.
 *
 * Drop it into a form, give it the lines, and it keeps a server-computed tax
 * breakup in sync as the user types. It exists so that no form ever again does
 * its own `taxable * rate / 100`:
 *
 *   - the rate lives in the tax masters, versioned by effective date;
 *   - whether it is CGST+SGST or IGST depends on the company's registration,
 *     the party's state and the place of supply — none of which the browser
 *     can see;
 *   - reverse charge, cess, exempt/nil/zero-rated and tax-inclusive pricing
 *     all change the arithmetic in ways a single percentage cannot express.
 *
 * Behaviour that matters in a form:
 *   - DEBOUNCED, so typing a quantity does not fire a request per keystroke.
 *   - RACE-SAFE: a slow earlier response can never overwrite a newer one.
 *   - Keeps the LAST GOOD result visible while a new one is in flight, so the
 *     totals strip does not blank out mid-edit.
 *   - Skips the call entirely when there is nothing billable yet.
 *
 * The result is advisory for display only. The server recomputes on save and
 * stores its own figures — this hook never sends totals back.
 */

export interface UseTaxCalculationOptions {
  /** Milliseconds to wait after the last change. 350ms reads as instant. */
  debounceMs?: number;
  /** Set false to hold off entirely (e.g. while the form is still loading). */
  enabled?: boolean;
}

export interface UseTaxCalculationResult {
  tax: TaxCalculation | null;
  loading: boolean;
  error: string | null;
  /** Force an immediate recalculation, bypassing the debounce. */
  refresh: () => void;
}

/** A line is worth sending once it has a positive quantity and a price. */
const isBillable = (l: TaxLineInput): boolean =>
  Number(l?.quantity) > 0 && Number(l?.unitPrice) >= 0
  && (l.productId != null || l.taxCategoryId != null || l.hsnCode != null || Number(l.unitPrice) > 0);

export function useTaxCalculation(
  input: TaxCalculationInput | null,
  { debounceMs = 350, enabled = true }: UseTaxCalculationOptions = {}
): UseTaxCalculationResult {
  const [tax, setTax] = useState<TaxCalculation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Monotonic request id. Only the newest response is allowed to win, so a
  // slow request for "qty 1" cannot land after the fast one for "qty 12" and
  // silently show the user the wrong total.
  const requestSeq = useRef(0);
  const [manualTrigger, setManualTrigger] = useState(0);

  const billableLines = useMemo(
    () => (input?.lines || []).filter(isBillable),
    [input?.lines]
  );

  // Serialising the payload gives a stable dependency: the effect re-runs when
  // the VALUES change, not when the caller happens to rebuild the array (which
  // a React form does on every render).
  const payloadKey = useMemo(() => {
    if (!input || billableLines.length === 0) return null;
    return JSON.stringify({ ...input, lines: billableLines });
  }, [input, billableLines]);

  useEffect(() => {
    if (!enabled || !payloadKey) {
      // Nothing billable yet — clear rather than leaving a stale total on
      // screen after the user empties the grid.
      setTax(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    const seq = ++requestSeq.current;
    setLoading(true);

    const timer = setTimeout(async () => {
      try {
        const result = await taxService.calculate(JSON.parse(payloadKey));
        if (seq !== requestSeq.current) return; // superseded
        setTax(result);
        setError(null);
      } catch (e: any) {
        if (seq !== requestSeq.current) return;
        // Keep the previous good breakup on screen; surfacing the message is
        // enough, blanking the totals mid-edit is not.
        setError(e?.response?.data?.message || e?.message || 'Could not calculate tax');
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [payloadKey, enabled, debounceMs, manualTrigger]);

  const refresh = useCallback(() => setManualTrigger((n) => n + 1), []);

  return { tax, loading, error, refresh };
}

export default useTaxCalculation;
